export const PRIVACY = {
  slug: "privacy",
  title: "Privacy & Cookie Policy",
  lastUpdated: "6 July 2026",
  intro:
    'This policy explains how Blue Shuck Consulting LTD ("Blue Shuck", "we", "us") collects, uses and protects personal data when you use our web-based takeoff, estimating and billing application (the "Service"). We are committed to handling your data in line with the UK GDPR and the Data Protection Act 2018.',
  sections: [
    {
      heading: "1. Who we are (Data Controller)",
      body: [
        "Blue Shuck Consulting LTD, 29 Glanville Drive, Houghton Le Spring, DH4 6NZ. Companies House number: 17308746. For any privacy questions or to exercise your rights, contact us at support@blueshuckconsulting.com.",
      ],
    },
    {
      heading: "2. The data we collect",
      bullets: [
        "Account data: your name, email address, company name, and a securely hashed password (we never store your password in plain text).",
        "Subscription data: your plan tier, subscription status, renewal dates, and Stripe customer/subscription identifiers. Card details are entered directly with Stripe — we never see or store them.",
        "Project & content data: project names, client names and site addresses you enter, blueprint files (PDFs/images) you upload, scale calibrations, takeoff measurements and counts, estimate line items, and progress billing records and notes.",
        "Client contact data you provide: email addresses you enter to send an Application for Payment to your own clients.",
        "Technical data: log data such as timestamps and error records generated to operate and secure the Service.",
      ],
    },
    {
      heading: "3. How and why we use your data",
      bullets: [
        "To provide and operate the Service, including authenticating you and storing your projects — lawful basis: performance of a contract.",
        "To process subscriptions and payments via Stripe — contract.",
        "To send transactional emails (welcome, password reset, and the Applications for Payment you choose to send) via our email provider — contract / legitimate interests.",
        "To keep the Service secure and prevent fraud or misuse — legitimate interests.",
        "To comply with our legal and regulatory obligations — legal obligation.",
      ],
    },
    {
      heading: "4. Sharing & sub-processors",
      body: [
        "We do not sell your data. We share it only with trusted providers who process it on our behalf under appropriate data-processing terms:",
      ],
      bullets: [
        "Stripe — subscription billing and card payments.",
        "Zoho — delivery of transactional emails.",
        "Cloud hosting, database and object storage providers — to host the application and store your projects and uploaded files.",
      ],
    },
    {
      heading: "5. International transfers",
      body: [
        "Where any provider processes data outside the UK, we rely on appropriate safeguards such as UK adequacy regulations or Standard Contractual Clauses to protect your data.",
      ],
    },
    {
      heading: "6. Data retention",
      body: [
        "We keep your data for as long as your account is active. On termination or account closure you have a 30-day window to export your project histories and estimate/billing registers (CSV/PDF), after which we may permanently purge the records from our storage. Password reset tokens expire within 1 hour. Residual copies in routine backups are deleted on our normal backup cycle.",
      ],
    },
    {
      heading: "7. Your rights",
      body: [
        "Under UK GDPR you have the right to access, rectify, erase, restrict or object to processing of your personal data, to data portability, and to withdraw consent where processing relies on it. To exercise any right, email support@blueshuckconsulting.com. You also have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk.",
      ],
    },
    {
      heading: "8. Security",
      body: [
        "We apply commercially reasonable technical and organisational safeguards, including encryption in transit (HTTPS), hashed passwords, access controls, and single-use, time-limited password reset links.",
      ],
    },
    {
      heading: "9. Cookies & tracking technologies",
      body: [
        "We keep cookies to a minimum and ask for your consent before setting any non-essential (analytics) cookies. When you first visit our website you are shown a cookie notice where you can accept or decline analytics.",
      ],
      bullets: [
        "Strictly necessary storage: within the Trade Off application we store a secure sign-in token in your browser's local storage to keep you logged in, and a small preference recording your cookie choice. These are essential and cannot be switched off.",
        "Analytics (consent-based): on our website we use Google Analytics (GA4) to understand how visitors use the site. These cookies are only set if you click 'Accept' on the cookie notice, and IP addresses are anonymised. If you decline, no analytics cookies are set and no analytics data is collected.",
        "Third-party checkout: when you pay, Stripe's hosted checkout pages may set their own strictly necessary cookies to process the payment securely. These are governed by Stripe's own privacy and cookie policies.",
      ],
      after: [
        "You can change your mind at any time by clearing your browser storage for this site, which will bring the cookie notice back on your next visit.",
      ],
    },
    {
      heading: "10. Children",
      body: [
        "The Service is intended for business use by professionals and is not directed at anyone under 18.",
      ],
    },
    {
      heading: "11. Changes to this policy",
      body: [
        'We may update this policy from time to time. Material changes will be reflected here with a revised "last updated" date.',
      ],
    },
  ],
};

export const TERMS = {
  slug: "terms",
  title: "Terms of Service Agreement",
  lastUpdated: "6 July 2026",
  intro:
    "These terms govern access to and use of the software provided by Blue Shuck Consulting LTD. By using the Service, you agree to the following terms.",
  sections: [
    {
      heading: "1. Services, Access & Blueprint Ingestion",
      body: [
        'SaaS License: Subject to the terms of this Agreement, Provider grants Client a non-exclusive, non-transferable, revocable right to access and use the web-based Takeoff-to-Billing application (the "Service") solely for your internal business operations (e.g., project estimation, material quantification, and client invoicing).',
        "Account Security: You are responsible for ensuring that all authorized users (such as your estimators or commercial managers) keep their login credentials confidential. Client is strictly liable for all activities, calculations, and financial submittals occurring under its accounts.",
        "System Requirements & Vector Graphics: The Service is optimized for web browser use on desktop devices to accommodate high-precision drawing scaling. Provider is not responsible for performance degradation resulting from the use of unsupported devices (such as small-screen mobile phones) to execute complex plan tracings.",
        "Modifications: Provider reserves the right to deploy updates, bug fixes, or feature enhancements (such as vector snapping improvements or updated data table tools) at any time, provided they do not materially degrade core functionality.",
      ],
    },
    {
      heading: "2. Accuracy of Estimations & Blueprint Calibration",
      body: [
        "User Calibration Responsibility: The Service provides digital tools allowing you to upload architectural PDF schematics and apply custom scale calibrations (e.g., 1:100). You acknowledge and agree that the mathematical accuracy of any vector measurement or linear/area takeoff is entirely dependent on the accuracy of the baseline scale set by the user.",
        "Bid and Invoicing Autonomy: All material cost lists, labor markups, and downstream Schedules of Values (SOV) generated by the system are internal operational aids. Client retains absolute responsibility for cross-checking quantities, reviewing physical project realities, and finalizing all contract valuations before submitting bids or payment applications to third parties.",
        "No Liability for Under-Quoting or Financial Loss: To the maximum extent permitted by law, Provider shall not be held liable for any commercial losses, project cost overruns, rejected payment applications, or unprofitable contracts resulting from faulty estimations, incorrect blueprint dimensions, or miscalculated unit rates executed within the platform.",
      ],
    },
    {
      heading: "3. Fees, Payment Processing & Subscription Tiers",
      body: ["Subscription Packages: The Service is billed based on a 3-tier structure linked to active project counts:"],
      bullets: [
        "Free Tier: Limited to a maximum of 1 active project at any time.",
        "Growth Tier (£29 / month): Limited to a maximum of 5 active projects.",
        "Scale Tier (£79 / month): Entitles the Client to unlimited project configurations.",
      ],
      after: [
        "Stripe Integration: Payment processing is handled securely through our third-party billing merchant, Stripe. By subscribing to a paid tier, you authorize Stripe to automatically charge your designated payment method on a recurring monthly or annual cycle.",
        "UK Framework Currency & VAT: All fees are explicitly billed and payable in Pound Sterling (GBP / £) and are exclusive of Value Added Tax (VAT), which will be added at the prevailing statutory rate where applicable.",
        "Late Payments & Suspension: Overdue balances on paid tiers may result in the automated restriction of your account permissions. If subscription charges fail and remain outstanding for more than 14 days, Provider reserves the right to downgrade the account to the Free Tier or suspend access entirely until outstanding balances are resolved.",
      ],
    },
    {
      heading: "4. Client Data, Intellectual Property & Data Protection",
      body: [
        'Data Ownership: Client retains all right, title, and interest in any architectural drawings, blueprint files, custom pricing matrices, and customer details uploaded to the database (the "Client Data").',
        "Data License to Provider: You grant Provider a limited, secure, worldwide license to host, parse, copy, and display Client Data solely as required to render the canvas viewports, calculate table metrics, and maintain your account.",
        "UK Data Protection & GDPR: Both parties agree to comply with their respective obligations under the UK GDPR and the Data Protection Act 2018. Provider will implement commercially reasonable technical and administrative safeguards to protect blueprint uploads and project databases stored in our infrastructure.",
        "Provider IP Protection: Provider owns and retains all intellectual property rights in the application, including the front-end scaling interface, the proprietary takeoff-to-billing logic engines, database designs, source code, and associated marketing text.",
      ],
    },
    {
      heading: "5. Cancellation & Termination",
      body: [
        "Self-Service Cancellation: You may cancel your subscription tier at any time directly through the platform's billing control panel or by notifying us in writing. Your access to the paid limitations will persist until the end of your current paid billing cycle.",
        "Data Retrieval Windows: Upon termination or deliberate closure of an account, Client will have a standard window of 30 days to download or export project histories and estimate registers as standard CSV/PDF formats. Following this 30-day grace period, Provider reserves the right to permanently purge obsolete database records from storage pools.",
      ],
    },
    {
      heading: "6. Limitation of Liability",
      body: [
        '"As-Is" General Provision: The application is provided to construction professionals on an "as-is" and "as-available" basis. Provider makes no guarantees regarding continuous uptime or that architectural drawing text scraping will be 100% error-free on heavily pixelated legacy documents.',
        "Liability Cap: To the maximum extent permitted by law, in no event shall Provider's total aggregate liability for all claims arising out of or relating to this Agreement exceed the total value of fees actually paid by the Client to the Provider in the twelve (12) months immediately preceding the claim trigger event.",
        "Consequential Loss Exclusion: Neither party shall be held liable to the other for indirect, special, or consequential damages, including but not limited to loss of business profits, lost construction tenders, reputational harm, or data loss.",
      ],
    },
    {
      heading: "7. Governing Law & Dispute Resolution",
      body: [
        "Jurisdiction: This Agreement, and any operational disputes or claims arising out of it, shall be governed exclusively by, and construed in accordance with, the laws of England and Wales.",
        "Venue: Both parties irrevocably agree that the courts of England and Wales shall hold exclusive jurisdiction to settle any disputes arising directly from application access or commercial discrepancies under these terms.",
      ],
    },
  ],
};

export const ACCESSIBILITY = {
  slug: "accessibility",
  title: "Accessibility Statement",
  lastUpdated: "6 July 2026",
  intro:
    "Blue Shuck Consulting LTD is committed to ensuring digital accessibility for all users, including individuals with visual, auditory, motor, or cognitive disabilities. We endeavour to continually improve the user experience for everyone and actively apply the relevant accessibility standards to our web application platform.",
  sections: [
    {
      heading: "Conformance Status",
      body: [
        "The Web Content Accessibility Guidelines (WCAG) define requirements for designers and developers to improve accessibility for people with disabilities. It defines three levels of conformance: Level A, Level AA, and Level AAA.",
        'The platform is partially conformant with WCAG 2.2 Level AA. "Partially conformant" means that while the vast majority of our core interface, billing screens, data tables, and navigation elements meet the accessibility standards, certain highly specialized graphical mapping features have technical limitations that we are actively working to resolve.',
      ],
    },
    {
      heading: "Accessibility Features in Our App",
      body: [
        "Across our core platform (including settings panels, billing portals, item master catalogs, and downstream invoicing grids), we implement the following features:",
      ],
      bullets: [
        "Text Alternatives: We provide alternative text (alt attributes) for non-decorative interface icons and operational graphics.",
        "Semantic Structure: Standard HTML layouts are used to support assistive technologies such as screen readers, maintaining consistent heading hierarchies.",
        "Predictable Navigation: Form controls, submission menus, and data entries follow predictable operational flows.",
        "Contrast Requirements: Text elements and core user interface boundaries are designed to respect the necessary luminosity contrast ratios against backgrounds.",
      ],
    },
    {
      heading: "Known Technical Limitations & Disclosures",
      body: [
        "Due to the highly specialized, graphical nature of a pre-construction estimation tool, users may encounter limitations within our Blueprint Takeoff Canvas:",
      ],
      bullets: [
        "Interactive Vector Canvas & Pointer Dependency: The digital measurement interface requires users to upload architectural PDFs, calibrate a precise baseline scale, and execute manual vector snapping. Because this feature is fundamentally dependent on precise spatial and fine-motor cursor coordination, it currently cannot be fully driven via a standard keyboard alone.",
        "Dense Tabular Data & Screen Scaling: The platform utilizes advanced interactive tables to track raw material costs and dynamic labor markups. While standard zooming up to 200% is supported across the application, scaling past this threshold on standard desktop viewports may require horizontal scrolling within complex layout grids.",
        "Complex Drawing Contexts: Legibility of screen reader descriptions may vary based on user-uploaded blueprints. The application reads user-defined labels and database entries perfectly, but it cannot programmatically translate un-scraped structural text buried deep inside dense legacy architectural raster drawings.",
      ],
    },
    {
      heading: "Feedback & Contact Information",
      body: [
        "We welcome your feedback on the accessibility of our software. If you experience any technical barriers or require data formats (such as specific CSV or estimate registers) in an alternative layout, please let us know:",
      ],
      bullets: [
        "Email: support@blueshuckconsulting.com",
        "Response Time: We aim to respond to accessibility inquiries within 2 business days.",
      ],
    },
    {
      heading: "Assessment and Continuous Improvement",
      body: [
        "We assess the accessibility of the platform through regular self-evaluations, automated code linter reviews, and iterative testing during our product sprint cycles. Our development roadmap actively prioritizes enhancing keyboard navigation alternatives and expanding screen reader definitions outside the immediate drawing viewport.",
      ],
    },
  ],
};

export const LEGAL_DOCS = {
  privacy: PRIVACY,
  terms: TERMS,
  accessibility: ACCESSIBILITY,
};
